import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { InvitationTemplateApiService } from '../services/invitation-template-api.service';
import {
  InvitationTemplateRequest,
  InvitationTemplatePreviewRequest,
  StylePreset,
} from '../models/invitation-template.model';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-invitation-template-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './invitation-template-form.component.html',
})
export class InvitationTemplateFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  templateId: number | null = null;

  // ── Vista previa (renderer autoritativo del backend, igual al correo real) ────
  showPreview = false;
  previewLoading = false;
  previewError = '';
  previewSafeHtml: SafeHtml | null = null;
  previewSubject = '';

  // ── Form fields ──────────────────────────────────────────────────────────────
  name = '';
  subject = '';
  bodyHtml = '';
  bodyText = '';
  stylePreset: StylePreset | '' = '';
  primaryColor = '#3B82F6';
  logoUrl = '';
  isDefault = false;

  readonly stylePresets: { value: StylePreset; label: string }[] = [
    { value: 'PROFESSIONAL', label: 'Profesional' },
    { value: 'CASUAL', label: 'Casual' },
    { value: 'MINIMAL', label: 'Minimal' },
    { value: 'BRANDED', label: 'Con marca' },
  ];

  readonly subjectPlaceholder = 'Ej: Te invitamos a unirte a {{business_name}}';
  readonly bodyHtmlPlaceholder =
    '<p>Hola, te invitamos a unirte a {{business_name}}...</p>';

  readonly placeholders = [
    { token: '{{invitation_link}}', desc: 'Enlace para aceptar la invitación' },
    // Si el cuerpo NO lo usa, el backend igual agrega un bloque con el código al
    // final del correo (InvitationEmailRenderer.codeBlock): el correo nunca sale
    // solo con el enlace. Ponerlo aquí sirve para ubicarlo donde uno quiera.
    { token: '{{invitation_code}}', desc: 'Código de invitación (el token del enlace)' },
    { token: '{{business_name}}', desc: 'Nombre de la empresa' },
    { token: '{{invited_by}}', desc: 'Nombre de quien invita' },
    { token: '{{role_name}}', desc: 'Rol asignado al invitado' },
    { token: '{{expires_at}}', desc: 'Fecha de expiración' },
    { token: '{{mail_invited}}', desc: 'Correo del invitado' },
  ];

  constructor(
    private templateApi: InvitationTemplateApiService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private toast: ToastService,
  ) {}

  /**
   * Abre la vista previa pidiendo al backend el HTML final (mismo renderer que el envío real),
   * así el logo, el color y el preset se reflejan idénticos al correo entregado.
   */
  abrirPreview(): void {
    if (!this.subject.trim() || !this.bodyHtml.trim()) {
      this.toast.warning({
        title: 'Campos incompletos',
        message:
          'Complete el asunto y el cuerpo HTML para ver la vista previa.',
      });
      return;
    }

    this.showPreview = true;
    this.previewLoading = true;
    this.previewError = '';
    this.previewSafeHtml = null;
    this.previewSubject = '';

    const request: InvitationTemplatePreviewRequest = {
      subject: this.subject.trim(),
      bodyHtml: this.bodyHtml.trim(),
      bodyText: this.bodyText.trim() || undefined,
      stylePreset: this.stylePreset || undefined,
      primaryColor: this.primaryColor || undefined,
      logoUrl: this.logoUrl.trim() || undefined,
    };

    this.templateApi.preview(request).subscribe({
      next: (res) => {
        this.previewSubject = res.subject;
        this.previewSafeHtml = this.sanitizer.bypassSecurityTrustHtml(res.html);
        this.previewLoading = false;
      },
      error: (err) => {
        this.previewError =
          err.error?.message || 'No se pudo generar la vista previa.';
        this.previewLoading = false;
      },
    });
  }

  cerrarPreview(): void {
    this.showPreview = false;
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.templateId = +id;
      this.cargarPlantilla(this.templateId);
    }
  }

  private cargarPlantilla(id: number): void {
    this.loading = true;
    this.error = '';

    this.templateApi.getById(id).subscribe({
      next: (tpl) => {
        this.name = tpl.name;
        this.subject = tpl.subject;
        this.bodyHtml = tpl.bodyHtml;
        this.bodyText = tpl.bodyText ?? '';
        this.stylePreset = tpl.stylePreset ?? '';
        this.primaryColor = tpl.primaryColor ?? '#3B82F6';
        this.logoUrl = tpl.logoUrl ?? '';
        this.isDefault = tpl.isDefault;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la plantilla';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Plantilla de Invitación'
      : 'Nueva Plantilla de Invitación';
  }

  guardar(): void {
    const faltantes: string[] = [];
    if (!this.name.trim()) faltantes.push('Nombre');
    if (!this.subject.trim()) faltantes.push('Asunto');
    if (!this.bodyHtml.trim()) faltantes.push('Cuerpo HTML');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const request: InvitationTemplateRequest = {
      name: this.name.trim(),
      subject: this.subject.trim(),
      bodyHtml: this.bodyHtml.trim(),
      bodyText: this.bodyText.trim() || undefined,
      stylePreset: this.stylePreset || undefined,
      primaryColor: this.primaryColor || undefined,
      logoUrl: this.logoUrl.trim() || undefined,
      isDefault: this.isDefault,
    };

    if (this.isEditing && this.templateId) {
      this.templateApi.update(this.templateId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Plantilla de invitación');
          this.router.navigate(['/invitation-templates']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la plantilla';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.templateApi.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Plantilla de invitación');
          this.router.navigate(['/invitation-templates']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la plantilla';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/invitation-templates']);
  }

  //
  private buildEmailHtml(): string {
    const color = this.primaryColor || '#3B82F6';
    const logo = this.logoUrl?.trim()
      ? `<div style="text-align:center;padding:24px 0;">
          <img src="${this.logoUrl.trim()}" alt="Logo"
                style="max-height:48px;object-fit:contain;" />
        </div>`
      : '';

    // El preset define el estilo del contenedor
    const presetStyles = this.getPresetStyles(this.stylePreset);

    return `<!DOCTYPE html>
  <html lang="es">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { margin:0; padding:0; background:#f4f4f7;
            font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif; }
      .email-wrapper { max-width:600px; margin:0 auto; background:#ffffff; ${presetStyles.wrapper} }
      .email-body { padding:24px; color:#111827; font-size:15px; line-height:1.6; }
      .email-body a { color:${color}; }
      .accent { color:${color}; }
      /* Botones / enlaces de acción usan el color primario */
      .email-body a[href*="invitation"], .btn-primary {
        display:inline-block; background:${color}; color:#ffffff !important;
        padding:12px 24px; border-radius:8px; text-decoration:none; font-weight:600;
      }
      .email-body img { max-width:100%; height:auto; }
    </style>
  </head>
  <body>
    <div class="email-wrapper">
      ${logo}
      <div class="email-body">
        ${this.bodyHtml}
      </div>
    </div>
  </body>
  </html>`;
  }

  private getPresetStyles(preset: StylePreset | ''): { wrapper: string } {
    switch (preset) {
      case 'PROFESSIONAL':
        return {
          wrapper: 'border-radius:0;box-shadow:0 1px 3px rgba(0,0,0,0.1);',
        };
      case 'CASUAL':
        return {
          wrapper: 'border-radius:16px;box-shadow:0 4px 12px rgba(0,0,0,0.08);',
        };
      case 'MINIMAL':
        return { wrapper: 'border-radius:0;box-shadow:none;' };
      case 'BRANDED':
        return {
          wrapper: `border-radius:8px;border-top:4px solid ${this.primaryColor || '#3B82F6'};`,
        };
      default:
        return {
          wrapper: 'border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.1);',
        };
    }
  }
}
